import React, {
    useState,
    useEffect,
    useCallback,
    useMemo
} from "react";

import {
    Card,
    Row,
    Col,
    AutoComplete,
    Input,
    Button,
    Table,
    Space,
    Typography,
    Statistic,
    Tag,
    Avatar,
    Empty,
    Spin,
    message,
    Divider,
    Badge,
    Tooltip
} from "antd";

import {
    SearchOutlined,
    DownloadOutlined,
    UserOutlined,
    DollarCircleOutlined,
    WalletOutlined,
    HistoryOutlined,
    CalendarOutlined,
    IdcardOutlined,
    BookOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    FileTextOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    CreditCardOutlined
} from "@ant-design/icons";

import api from "../../api/api";

import {
    useAcademicYear
} from "../../AcademicYearContext";

import {
    PDFDownloadLink
} from "@react-pdf/renderer";

import StudentPaymentHistoryPDF from "./StudentPaymentHistoryPDF";

const {
    Title,
    Text
} = Typography;

const COLORS = {
    black: "#09090b",
    deepGray: "#18181b",
    charcoal: "#27272a",
    orange: "#f97316",
    deepOrange: "#ea580c",
    green: "#16a34a",
    lime: "#84cc16",
    gold: "#facc15",
    white: "#ffffff",
    background: "#f5f7fa",
    border: "#e5e7eb",
    muted: "#71717a",
    blue: "#2563eb",
    red: "#dc2626"
};

const currency = (value) => {
    const amount = Number(value || 0);

    return `$${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

const StudentPaymentHistory = () => {
    const { selectedYear } = useAcademicYear();

    const [students, setStudents] = useState([]);
    const [studentSearch, setStudentSearch] = useState("");
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [loadingStudents, setLoadingStudents] = useState(false);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [downloading, setDownloading] = useState(false);

    const fetchStudents = useCallback(
        async (value) => {
            setStudentSearch(value);

            if (!value || !value.trim()) {
                setStudents([]);
                return;
            }

            try {
                setLoadingStudents(true);

                const response = await api.get(
                    "/student-payment-history",
                    {
                        params: {
                            search: value,
                            academic_year: selectedYear?.id
                        }
                    }
                );

                const results =
                    response.data?.results ||
                    response.data ||
                    [];

                setStudents(
                    Array.isArray(results)
                        ? results
                        : []
                );
            } catch (error) {
                console.error(error);

                message.error(
                    "Unable to search students."
                );
            } finally {
                setLoadingStudents(false);
            }
        },
        [selectedYear]
    );

    const fetchPaymentHistory = useCallback(
        async (studentId) => {
            if (!studentId) {
                setTransactions([]);
                return;
            }

            try {
                setLoadingTransactions(true);

                const response = await api.get(
                    "/student-payment-history/",
                    {
                        params: {
                            student: studentId,
                            academic_year: selectedYear?.id
                        }
                    }
                );

                const results =
                    response.data?.results ||
                    response.data ||
                    [];

                setTransactions(
                    Array.isArray(results)
                        ? results
                        : []
                );
            } catch (error) {
                console.error(error);

                message.error(
                    "Unable to load payment history."
                );
            } finally {
                setLoadingTransactions(false);
            }
        },
        [selectedYear]
    );

    const handleStudentSelect = (
        value,
        option
    ) => {
        const student = option?.student;

        if (!student) {
            return;
        }

        setSelectedStudent(student);

        fetchPaymentHistory(student.id);
    };

    const handleClearStudent = () => {
        setSelectedStudent(null);
        setTransactions([]);
        setStudentSearch("");
        setStudents([]);
    };

    const handleRefresh = () => {
        if (!selectedStudent) {
            message.info(
                "Select a student first."
            );
            return;
        }

        fetchPaymentHistory(
            selectedStudent.id
        );
    };

    const handleDownload = () => {
        if (!selectedStudent) {
            message.warning(
                "Please select a student."
            );

            return;
        }

        setDownloading(true);

        setTimeout(() => {
            setDownloading(false);
        }, 1500);
    };

    useEffect(() => {
        if (selectedStudent) {
            fetchPaymentHistory(
                selectedStudent.id
            );
        }
    }, [
        selectedYear,
        selectedStudent,
        fetchPaymentHistory
    ]);

    const totalPaid = useMemo(() => {
        if (!Array.isArray(transactions)) {
            return 0;
        }

        return transactions.reduce(
            (total, item) =>
                total +
                Number(
                    item.amount_paid || 0
                ),
            0
        );
    }, [transactions]);

    const totalBalance = useMemo(() => {
        if (!Array.isArray(transactions)) {
            return 0;
        }

        return transactions.reduce(
            (total, item) =>
                total +
                Number(
                    item.balance || 0
                ),
            0
        );
    }, [transactions]);

    const averagePayment = useMemo(() => {
        if (!transactions.length) {
            return 0;
        }

        return totalPaid / transactions.length;
    }, [
        totalPaid,
        transactions.length
    ]);

    const latestPayment = useMemo(() => {
        if (!transactions.length) {
            return null;
        }

        return transactions[0];
    }, [transactions]);

    const studentOptions = students.map(
        (student) => ({
            value: student.full_name,
            label: (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "5px 0"
                    }}
                >
                    <Avatar
                        size={36}
                        src={
                            student.student_image ||
                            undefined
                        }
                        icon={
                            !student.student_image
                                ? <UserOutlined />
                                : undefined
                        }
                        style={{
                            background:
                                "linear-gradient(135deg,#f97316,#84cc16)"
                        }}
                    />

                    <div
                        style={{
                            minWidth: 0
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 700,
                                color: "#18181b"
                            }}
                        >
                            {student.full_name}
                        </div>

                        <Text
                            type="secondary"
                            style={{
                                fontSize: 12
                            }}
                        >
                            ID: {student.id}
                            {" • "}
                            {student.grade_class_name ||
                                student.grade_class ||
                                "N/A"}
                        </Text>
                    </div>
                </div>
            ),
            student
        })
    );

    const columns = [
        {
            title: "#",
            width: 60,
            fixed: "left",
            render: (_, __, index) => (
                <Text
                    strong
                    style={{
                        color: COLORS.muted
                    }}
                >
                    {index + 1}
                </Text>
            )
        },
        {
            title: "Transaction",
            key: "transaction",
            width: 210,
            render: (_, record) => (
                <Space
                    align="start"
                    size={12}
                >
                    <div
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 11,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                                "rgba(249,115,22,0.10)",
                            color:
                                COLORS.orange,
                            flexShrink: 0
                        }}
                    >
                        <CreditCardOutlined />
                    </div>

                    <div>
                        <Text
                            strong
                            style={{
                                display: "block"
                            }}
                        >
                            {record.fee_type ||
                                "Payment"}
                        </Text>

                        <Text
                            type="secondary"
                            style={{
                                fontSize: 12
                            }}
                        >
                            {record.payment_category ||
                                "General"}
                        </Text>
                    </div>
                </Space>
            )
        },
        {
            title: "Date",
            dataIndex: "created_at",
            width: 160,
            render: (value) => (
                <Space size={8}>
                    <CalendarOutlined
                        style={{
                            color:
                                COLORS.orange
                        }}
                    />

                    <Text>
                        {value || "N/A"}
                    </Text>
                </Space>
            )
        },
        {
            title: "Category",
            dataIndex:
                "payment_category",
            width: 150,
            render: (value) => (
                <Tag
                    style={{
                        borderRadius: 20,
                        padding:
                            "3px 10px",
                        fontWeight: 600
                    }}
                    color={
                        value === "School Fee"
                            ? "orange"
                            : "green"
                    }
                >
                    {value || "General"}
                </Tag>
            )
        },
        {
            title: "Amount Paid",
            dataIndex: "amount_paid",
            align: "right",
            width: 150,
            render: (value) => (
                <Text
                    strong
                    style={{
                        color:
                            COLORS.green,
                        fontSize: 14
                    }}
                >
                    {currency(value)}
                </Text>
            )
        },
        {
            title: "Balance",
            dataIndex: "balance",
            align: "right",
            width: 150,
            render: (value) => {
                const amount =
                    Number(value || 0);

                return (
                    <Text
                        strong
                        style={{
                            color:
                                amount > 0
                                    ? COLORS.red
                                    : COLORS.green
                        }}
                    >
                        {currency(amount)}
                    </Text>
                );
            }
        },
        {
            title: "Cashier",
            dataIndex: "created_by",
            width: 150,
            render: (value) => (
                <Space size={7}>
                    <Avatar
                        size={28}
                        icon={<UserOutlined />}
                        style={{
                            background:
                                COLORS.charcoal
                        }}
                    />

                    <Text>
                        {value || "N/A"}
                    </Text>
                </Space>
            )
        },
        {
            title: "Academic Year",
            dataIndex:
                "academic_year",
            width: 130,
            render: (value) => (
                <Tag
                    color="blue"
                    style={{
                        borderRadius: 20
                    }}
                >
                    {value ||
                        selectedYear?.name ||
                        "N/A"}
                </Tag>
            )
        }
    ];

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    "linear-gradient(180deg,#f8fafc 0%,#eef2f6 100%)",
                padding:
                    "24px"
            }}
        >
            <div
                style={{
                    maxWidth: 1600,
                    margin: "0 auto"
                }}
            >
                <Card
                    bordered={false}
                    style={{
                        borderRadius: 24,
                        overflow: "hidden",
                        boxShadow:
                            "0 18px 60px rgba(15,23,42,0.10)"
                    }}
                    bodyStyle={{
                        padding: 0
                    }}
                >
                    <div
                        style={{
                            position: "relative",
                            overflow: "hidden",
                            padding:
                                "30px 32px",
                            background:
                                "linear-gradient(135deg,#09090b 0%,#18181b 52%,#ea580c 150%)"
                        }}
                    >
                        <div
                            style={{
                                position:
                                    "absolute",
                                width: 260,
                                height: 260,
                                borderRadius:
                                    "50%",
                                background:
                                    "rgba(249,115,22,0.15)",
                                right: -80,
                                top: -120
                            }}
                        />

                        <div
                            style={{
                                position:
                                    "absolute",
                                width: 180,
                                height: 180,
                                borderRadius:
                                    "50%",
                                background:
                                    "rgba(132,204,22,0.10)",
                                right: 160,
                                bottom: -120
                            }}
                        />

                        <Row
                            gutter={[
                                24,
                                24
                            ]}
                            align="middle"
                        >
                            <Col
                                xs={24}
                                lg={11}
                            >
                                <Space
                                    direction="vertical"
                                    size={5}
                                >
                                    <Space
                                        size={10}
                                    >
                                        <div
                                            style={{
                                                width: 46,
                                                height: 46,
                                                borderRadius: 14,
                                                display: "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                background:
                                                    "linear-gradient(135deg,#f97316,#84cc16)",
                                                color:
                                                    "#fff",
                                                fontSize: 21
                                            }}
                                        >
                                            <WalletOutlined />
                                        </div>

                                        <Tag
                                            color="green"
                                            style={{
                                                border: 0,
                                                borderRadius: 20,
                                                fontWeight: 700
                                            }}
                                        >
                                            FINANCE
                                        </Tag>
                                    </Space>

                                    <Title
                                        level={2}
                                        style={{
                                            margin:
                                                "12px 0 0",
                                            color:
                                                "#fff",
                                            fontWeight:
                                                800,
                                            letterSpacing:
                                                "-0.5px"
                                        }}
                                    >
                                        Student Payment History
                                    </Title>

                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.70)",
                                            fontSize: 14
                                        }}
                                    >
                                        Review, analyze and
                                        generate complete
                                        student financial
                                        statements.
                                    </Text>
                                </Space>
                            </Col>

                            <Col
                                xs={24}
                                lg={13}
                            >
                                <div
                                    style={{
                                        display:
                                            "flex",
                                        justifyContent:
                                            "flex-end",
                                        gap: 10,
                                        flexWrap:
                                            "wrap"
                                    }}
                                >
                                    <AutoComplete
                                        value={
                                            studentSearch
                                        }
                                        style={{
                                            width:
                                                "min(100%, 430px)"
                                        }}
                                        options={
                                            studentOptions
                                        }
                                        onSearch={
                                            fetchStudents
                                        }
                                        onSelect={
                                            handleStudentSelect
                                        }
                                        onClear={() => {
                                            setStudentSearch(
                                                ""
                                            );
                                            setStudents(
                                                []
                                            );
                                        }}
                                        allowClear
                                        notFoundContent={
                                            loadingStudents ? (
                                                <div
                                                    style={{
                                                        padding:
                                                            10,
                                                        textAlign:
                                                            "center"
                                                    }}
                                                >
                                                    <Spin
                                                        size="small"
                                                    />
                                                </div>
                                            ) : (
                                                <Empty
                                                    image={
                                                        Empty.PRESENTED_IMAGE_SIMPLE
                                                    }
                                                    description="No students found"
                                                />
                                            )
                                        }
                                    >
                                        <Input
                                            size="large"
                                            prefix={
                                                <SearchOutlined
                                                    style={{
                                                        color:
                                                            COLORS.orange
                                                    }}
                                                />
                                            }
                                            placeholder="Search student by name or ID..."
                                            style={{
                                                borderRadius:
                                                    13,
                                                height:
                                                    48
                                            }}
                                        />
                                    </AutoComplete>

                                    <Tooltip
                                        title="Refresh payment history"
                                    >
                                        <Button
                                            size="large"
                                            icon={
                                                <ReloadOutlined />
                                            }
                                            onClick={
                                                handleRefresh
                                            }
                                            style={{
                                                height:
                                                    48,
                                                borderRadius:
                                                    13,
                                                background:
                                                    "rgba(255,255,255,0.10)",
                                                color:
                                                    "#fff",
                                                border:
                                                    "1px solid rgba(255,255,255,0.18)"
                                            }}
                                        />
                                    </Tooltip>

                                    {selectedStudent && (
                                        <PDFDownloadLink
                                            document={
                                                <StudentPaymentHistoryPDF
                                                    student={
                                                        selectedStudent
                                                    }
                                                    transactions={
                                                        transactions
                                                    }
                                                    academicYear={
                                                        selectedYear
                                                    }
                                                />
                                            }
                                            fileName={`Student_Payment_History_${selectedStudent.full_name}.pdf`}
                                        >
                                            {({
                                                loading
                                            }) => (
                                                <Button
                                                    size="large"
                                                    type="primary"
                                                    icon={
                                                        <DownloadOutlined />
                                                    }
                                                    loading={
                                                        loading ||
                                                        downloading
                                                    }
                                                    onClick={
                                                        handleDownload
                                                    }
                                                    style={{
                                                        height:
                                                            48,
                                                        borderRadius:
                                                            13,
                                                        border:
                                                            0,
                                                        background:
                                                            "linear-gradient(135deg,#f97316,#ea580c)",
                                                        fontWeight:
                                                            700,
                                                        boxShadow:
                                                            "0 8px 25px rgba(249,115,22,0.28)"
                                                    }}
                                                >
                                                    Download Statement
                                                </Button>
                                            )}
                                        </PDFDownloadLink>
                                    )}
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <div
                        style={{
                            padding:
                                "24px 28px 30px"
                        }}
                    >
                        <Row
                            gutter={[
                                16,
                                16
                            ]}
                        >
                            <Col
                                xs={24}
                                sm={12}
                                xl={6}
                            >
                                <Card
                                    bordered={false}
                                    style={{
                                        height: "100%",
                                        borderRadius: 18,
                                        background:
                                            "linear-gradient(135deg,#18181b,#27272a)",
                                        boxShadow:
                                            "0 10px 25px rgba(0,0,0,0.10)"
                                    }}
                                >
                                    <Statistic
                                        title={
                                            <Text
                                                style={{
                                                    color:
                                                        "rgba(255,255,255,0.65)"
                                                }}
                                            >
                                                Transactions
                                            </Text>
                                        }
                                        value={
                                            transactions.length
                                        }
                                        prefix={
                                            <HistoryOutlined
                                                style={{
                                                    color:
                                                        COLORS.orange
                                                }}
                                            />
                                        }
                                        valueStyle={{
                                            color:
                                                "#fff",
                                            fontWeight:
                                                800,
                                            fontSize:
                                                27
                                        }}
                                    />

                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.48)",
                                            fontSize: 12
                                        }}
                                    >
                                        Recorded payments
                                    </Text>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                xl={6}
                            >
                                <Card
                                    bordered={false}
                                    style={{
                                        height: "100%",
                                        borderRadius: 18,
                                        background:
                                            "linear-gradient(135deg,#ea580c,#f97316)",
                                        boxShadow:
                                            "0 10px 25px rgba(249,115,22,0.20)"
                                    }}
                                >
                                    <Statistic
                                        title={
                                            <Text
                                                style={{
                                                    color:
                                                        "rgba(255,255,255,0.78)"
                                                }}
                                            >
                                                Total Paid
                                            </Text>
                                        }
                                        value={
                                            totalPaid
                                        }
                                        precision={2}
                                        prefix={
                                            <DollarCircleOutlined
                                                style={{
                                                    color:
                                                        "#fff"
                                                }}
                                            />
                                        }
                                        valueStyle={{
                                            color:
                                                "#fff",
                                            fontWeight:
                                                800,
                                            fontSize:
                                                27
                                        }}
                                    />

                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.70)",
                                            fontSize: 12
                                        }}
                                    >
                                        Cumulative payments
                                    </Text>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                xl={6}
                            >
                                <Card
                                    bordered={false}
                                    style={{
                                        height: "100%",
                                        borderRadius: 18,
                                        background:
                                            "#fff",
                                        border:
                                            "1px solid #e5e7eb",
                                        boxShadow:
                                            "0 8px 24px rgba(15,23,42,0.06)"
                                    }}
                                >
                                    <Statistic
                                        title={
                                            <Text
                                                type="secondary"
                                            >
                                                Outstanding
                                            </Text>
                                        }
                                        value={
                                            totalBalance
                                        }
                                        precision={2}
                                        prefix={
                                            <WalletOutlined
                                                style={{
                                                    color:
                                                        totalBalance >
                                                        0
                                                            ? COLORS.red
                                                            : COLORS.green
                                                }}
                                            />
                                        }
                                        valueStyle={{
                                            color:
                                                totalBalance >
                                                0
                                                    ? COLORS.red
                                                    : COLORS.green,
                                            fontWeight:
                                                800,
                                            fontSize:
                                                27
                                        }}
                                    />

                                    <Text
                                        type="secondary"
                                        style={{
                                            fontSize: 12
                                        }}
                                    >
                                        Current balance
                                    </Text>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                xl={6}
                            >
                                <Card
                                    bordered={false}
                                    style={{
                                        height: "100%",
                                        borderRadius: 18,
                                        background:
                                            "linear-gradient(135deg,#65a30d,#22c55e)",
                                        boxShadow:
                                            "0 10px 25px rgba(34,197,94,0.18)"
                                    }}
                                >
                                    <Statistic
                                        title={
                                            <Text
                                                style={{
                                                    color:
                                                        "rgba(255,255,255,0.76)"
                                                }}
                                            >
                                                Academic Year
                                            </Text>
                                        }
                                        value={
                                            selectedYear?.name ||
                                            "N/A"
                                        }
                                        prefix={
                                            <CalendarOutlined
                                                style={{
                                                    color:
                                                        "#fff"
                                                }}
                                            />
                                        }
                                        valueStyle={{
                                            color:
                                                "#fff",
                                            fontWeight:
                                                800,
                                            fontSize:
                                                23
                                        }}
                                    />

                                    <Text
                                        style={{
                                            color:
                                                "rgba(255,255,255,0.70)",
                                            fontSize: 12
                                        }}
                                    >
                                        Active reporting period
                                    </Text>
                                </Card>
                            </Col>
                        </Row>

                        <Card
                            bordered={false}
                            style={{
                                marginTop: 22,
                                borderRadius: 20,
                                background:
                                    "#fff",
                                border:
                                    "1px solid #e5e7eb",
                                boxShadow:
                                    "0 8px 30px rgba(15,23,42,0.05)"
                            }}
                        >
                            <Row
                                gutter={[
                                    24,
                                    24
                                ]}
                                align="middle"
                            >
                                <Col
                                    xs={24}
                                    lg={15}
                                >
                                    <Space
                                        align="center"
                                        size={18}
                                    >
                                        <Avatar
                                            size={82}
                                            src={
                                                selectedStudent?.student_image ||
                                                undefined
                                            }
                                            icon={
                                                !selectedStudent?.student_image ? (
                                                    <UserOutlined />
                                                ) : undefined
                                            }
                                            style={{
                                                background:
                                                    "linear-gradient(135deg,#f97316,#84cc16)",
                                                border:
                                                    "4px solid #fff",
                                                boxShadow:
                                                    "0 6px 18px rgba(15,23,42,0.12)"
                                            }}
                                        />

                                        <div>
                                            <Space
                                                size={8}
                                                wrap
                                            >
                                                <Title
                                                    level={3}
                                                    style={{
                                                        margin: 0,
                                                        fontWeight:
                                                            800
                                                    }}
                                                >
                                                    {selectedStudent?.full_name ||
                                                        "No Student Selected"}
                                                </Title>

                                                {selectedStudent && (
                                                    <Badge
                                                        status="success"
                                                        text={
                                                            <Text
                                                                style={{
                                                                    color:
                                                                        COLORS.green,
                                                                    fontWeight:
                                                                        600
                                                                }}
                                                            >
                                                                Active Record
                                                            </Text>
                                                        }
                                                    />
                                                )}
                                            </Space>

                                            <div
                                                style={{
                                                    marginTop:
                                                        7
                                                }}
                                            >
                                                <Space
                                                    size={16}
                                                    wrap
                                                >
                                                    <Text
                                                        type="secondary"
                                                    >
                                                        <IdcardOutlined
                                                            style={{
                                                                marginRight:
                                                                    5
                                                            }}
                                                        />
                                                        Admission:
                                                        {" "}
                                                        <Text
                                                            strong
                                                        >
                                                            {selectedStudent?.id ||
                                                                "N/A"}
                                                        </Text>
                                                    </Text>

                                                    <Text
                                                        type="secondary"
                                                    >
                                                        <BookOutlined
                                                            style={{
                                                                marginRight:
                                                                    5
                                                            }}
                                                        />
                                                        Grade:
                                                        {" "}
                                                        <Text
                                                            strong
                                                        >
                                                            {selectedStudent?.grade_class_name ||
                                                                selectedStudent?.grade_class ||
                                                                "N/A"}
                                                        </Text>
                                                    </Text>
                                                </Space>
                                            </div>

                                            <Text
                                                type="secondary"
                                                style={{
                                                    display:
                                                        "block",
                                                    marginTop:
                                                        5
                                                }}
                                            >
                                                Student financial account
                                                for{" "}
                                                {selectedYear?.name ||
                                                    "the selected academic year"}
                                            </Text>
                                        </div>
                                    </Space>
                                </Col>

                                <Col
                                    xs={24}
                                    lg={9}
                                >
                                    <div
                                        style={{
                                            display:
                                                "flex",
                                            justifyContent:
                                                "flex-end",
                                            gap: 10,
                                            flexWrap:
                                                "wrap"
                                        }}
                                    >
                                        <div
                                            style={{
                                                padding:
                                                    "11px 15px",
                                                borderRadius:
                                                    12,
                                                background:
                                                    "#f8fafc",
                                                border:
                                                    "1px solid #e5e7eb"
                                            }}
                                        >
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display:
                                                        "block",
                                                    fontSize:
                                                        11
                                                }}
                                            >
                                                STATEMENT DATE
                                            </Text>

                                            <Text
                                                strong
                                            >
                                                {new Date().toLocaleDateString()}
                                            </Text>
                                        </div>

                                        {selectedStudent && (
                                            <Button
                                                danger
                                                type="text"
                                                onClick={
                                                    handleClearStudent
                                                }
                                            >
                                                Clear Student
                                            </Button>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        <Row
                            gutter={[
                                16,
                                16
                            ]}
                            style={{
                                marginTop: 18
                            }}
                        >
                            <Col
                                xs={24}
                                md={8}
                            >
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{
                                        borderRadius: 15,
                                        background:
                                            "#f8fafc",
                                        border:
                                            "1px solid #edf0f3"
                                    }}
                                >
                                    <Space
                                        align="start"
                                        size={12}
                                    >
                                        <div
                                            style={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: 10,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                background:
                                                    "rgba(37,99,235,0.10)",
                                                color:
                                                    COLORS.blue
                                            }}
                                        >
                                            <DollarCircleOutlined />
                                        </div>

                                        <div>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display:
                                                        "block",
                                                    fontSize:
                                                        12
                                                }}
                                            >
                                                Average Payment
                                            </Text>

                                            <Text
                                                strong
                                                style={{
                                                    fontSize:
                                                        17
                                                }}
                                            >
                                                {currency(
                                                    averagePayment
                                                )}
                                            </Text>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{
                                        borderRadius: 15,
                                        background:
                                            "#f8fafc",
                                        border:
                                            "1px solid #edf0f3"
                                    }}
                                >
                                    <Space
                                        align="start"
                                        size={12}
                                    >
                                        <div
                                            style={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: 10,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                background:
                                                    "rgba(22,163,74,0.10)",
                                                color:
                                                    COLORS.green
                                            }}
                                        >
                                            <ArrowUpOutlined />
                                        </div>

                                        <div>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display:
                                                        "block",
                                                    fontSize:
                                                        12
                                                }}
                                            >
                                                Latest Payment
                                            </Text>

                                            <Text
                                                strong
                                                style={{
                                                    fontSize:
                                                        17
                                                }}
                                            >
                                                {latestPayment
                                                    ? currency(
                                                          latestPayment.amount_paid
                                                      )
                                                    : "$0.00"}
                                            </Text>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>

                            <Col
                                xs={24}
                                md={8}
                            >
                                <Card
                                    size="small"
                                    bordered={false}
                                    style={{
                                        borderRadius: 15,
                                        background:
                                            "#f8fafc",
                                        border:
                                            "1px solid #edf0f3"
                                    }}
                                >
                                    <Space
                                        align="start"
                                        size={12}
                                    >
                                        <div
                                            style={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: 10,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                background:
                                                    totalBalance >
                                                    0
                                                        ? "rgba(220,38,38,0.10)"
                                                        : "rgba(22,163,74,0.10)",
                                                color:
                                                    totalBalance >
                                                    0
                                                        ? COLORS.red
                                                        : COLORS.green
                                            }}
                                        >
                                            <ArrowDownOutlined />
                                        </div>

                                        <div>
                                            <Text
                                                type="secondary"
                                                style={{
                                                    display:
                                                        "block",
                                                    fontSize:
                                                        12
                                                }}
                                            >
                                                Account Status
                                            </Text>

                                            <Text
                                                strong
                                                style={{
                                                    fontSize:
                                                        17,
                                                    color:
                                                        totalBalance >
                                                        0
                                                            ? COLORS.red
                                                            : COLORS.green
                                                }}
                                            >
                                                {totalBalance >
                                                0
                                                    ? "Balance Due"
                                                    : "Account Clear"}
                                            </Text>
                                        </div>
                                    </Space>
                                </Card>
                            </Col>
                        </Row>

                        <Card
                            bordered={false}
                            style={{
                                marginTop: 22,
                                borderRadius: 20,
                                overflow: "hidden",
                                border:
                                    "1px solid #e5e7eb",
                                boxShadow:
                                    "0 8px 30px rgba(15,23,42,0.05)"
                            }}
                            bodyStyle={{
                                padding: 0
                            }}
                        >
                            <div
                                style={{
                                    padding:
                                        "20px 22px",
                                    borderBottom:
                                        "1px solid #edf0f3",
                                    background:
                                        "#fff"
                                }}
                            >
                                <Row
                                    justify="space-between"
                                    align="middle"
                                    gutter={[
                                        12,
                                        12
                                    ]}
                                >
                                    <Col>
                                        <Space
                                            size={12}
                                        >
                                            <div
                                                style={{
                                                    width: 42,
                                                    height: 42,
                                                    borderRadius: 12,
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",
                                                    background:
                                                        "rgba(249,115,22,0.10)",
                                                    color:
                                                        COLORS.orange
                                                }}
                                            >
                                                <FileTextOutlined />
                                            </div>

                                            <div>
                                                <Title
                                                    level={4}
                                                    style={{
                                                        margin: 0,
                                                        fontWeight:
                                                            800
                                                    }}
                                                >
                                                    Payment Transactions
                                                </Title>

                                                <Text
                                                    type="secondary"
                                                >
                                                    Complete payment
                                                    activity for the
                                                    selected student.
                                                </Text>
                                            </div>
                                        </Space>
                                    </Col>

                                    <Col>
                                        <Tag
                                            color={
                                                transactions.length
                                                    ? "green"
                                                    : "default"
                                            }
                                            style={{
                                                borderRadius:
                                                    20,
                                                padding:
                                                    "4px 12px",
                                                fontWeight:
                                                    600
                                            }}
                                        >
                                            {transactions.length}{" "}
                                            Transaction
                                            {transactions.length !==
                                            1
                                                ? "s"
                                                : ""}
                                        </Tag>
                                    </Col>
                                </Row>
                            </div>

                            <Table
                                rowKey={(record) =>
                                    `${record.id || record.payment_category}-${record.created_at || Math.random()}`
                                }
                                columns={columns}
                                dataSource={
                                    transactions
                                }
                                loading={
                                    loadingTransactions
                                }
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                    pageSizeOptions: [
                                        "10",
                                        "20",
                                        "50",
                                        "100"
                                    ],
                                    showTotal:
                                        (total) =>
                                            `Showing ${total} transaction${total !== 1 ? "s" : ""}`
                                }}
                                locale={{
                                    emptyText: (
                                        <div
                                            style={{
                                                padding:
                                                    "55px 20px"
                                            }}
                                        >
                                            <Empty
                                                image={
                                                    Empty.PRESENTED_IMAGE_SIMPLE
                                                }
                                                description={
                                                    selectedStudent
                                                        ? "No payment history available for this student."
                                                        : "Search and select a student to view payment history."
                                                }
                                            />
                                        </div>
                                    )
                                }}
                                scroll={{
                                    x: 1200
                                }}
                                size="middle"
                            />
                        </Card>

                        {selectedStudent &&
                            transactions.length >
                                0 && (
                                <Card
                                    bordered={false}
                                    style={{
                                        marginTop: 22,
                                        borderRadius: 20,
                                        background:
                                            "linear-gradient(135deg,#09090b,#18181b)",
                                        color: "#fff",
                                        overflow:
                                            "hidden",
                                        boxShadow:
                                            "0 12px 35px rgba(0,0,0,0.15)"
                                    }}
                                >
                                    <Row
                                        gutter={[
                                            30,
                                            30
                                        ]}
                                    >
                                        <Col
                                            xs={24}
                                            lg={12}
                                        >
                                            <Space
                                                direction="vertical"
                                                size={15}
                                            >
                                                <Space
                                                    size={10}
                                                >
                                                    <SafetyCertificateOutlined
                                                        style={{
                                                            color:
                                                                COLORS.lime,
                                                            fontSize:
                                                                21
                                                        }}
                                                    />

                                                    <Text
                                                        style={{
                                                            color:
                                                                "#fff",
                                                            fontWeight:
                                                                700
                                                        }}
                                                    >
                                                        Financial Summary
                                                    </Text>
                                                </Space>

                                                <div>
                                                    <Text
                                                        style={{
                                                            color:
                                                                "rgba(255,255,255,0.55)",
                                                            display:
                                                                "block"
                                                        }}
                                                    >
                                                        Total Amount Paid
                                                    </Text>

                                                    <Text
                                                        style={{
                                                            color:
                                                                COLORS.orange,
                                                            fontSize:
                                                                30,
                                                            fontWeight:
                                                                800
                                                        }}
                                                    >
                                                        {currency(
                                                            totalPaid
                                                        )}
                                                    </Text>
                                                </div>

                                                <Divider
                                                    style={{
                                                        borderColor:
                                                            "rgba(255,255,255,0.10)",
                                                        margin:
                                                            "4px 0"
                                                    }}
                                                />

                                                <Space
                                                    size={30}
                                                    wrap
                                                >
                                                    <div>
                                                        <Text
                                                            style={{
                                                                color:
                                                                    "rgba(255,255,255,0.50)",
                                                                display:
                                                                    "block",
                                                                fontSize:
                                                                    12
                                                            }}
                                                        >
                                                            TRANSACTIONS
                                                        </Text>

                                                        <Text
                                                            style={{
                                                                color:
                                                                    "#fff",
                                                                fontSize:
                                                                    20,
                                                                fontWeight:
                                                                    700
                                                            }}
                                                        >
                                                            {
                                                                transactions.length
                                                            }
                                                        </Text>
                                                    </div>

                                                    <div>
                                                        <Text
                                                            style={{
                                                                color:
                                                                    "rgba(255,255,255,0.50)",
                                                                display:
                                                                    "block",
                                                                fontSize:
                                                                    12
                                                            }}
                                                        >
                                                            BALANCE
                                                        </Text>

                                                        <Text
                                                            style={{
                                                                color:
                                                                    totalBalance >
                                                                    0
                                                                        ? "#f87171"
                                                                        : "#4ade80",
                                                                fontSize:
                                                                    20,
                                                                fontWeight:
                                                                    700
                                                            }}
                                                        >
                                                            {currency(
                                                                totalBalance
                                                            )}
                                                        </Text>
                                                    </div>
                                                </Space>
                                            </Space>
                                        </Col>

                                        <Col
                                            xs={24}
                                            lg={12}
                                        >
                                            <div
                                                style={{
                                                    height:
                                                        "100%",
                                                    padding:
                                                        "20px",
                                                    borderRadius:
                                                        16,
                                                    background:
                                                        "rgba(255,255,255,0.05)",
                                                    border:
                                                        "1px solid rgba(255,255,255,0.08)"
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        color:
                                                            "#fff",
                                                        fontSize:
                                                            16,
                                                        fontWeight:
                                                            700
                                                    }}
                                                >
                                                    Statement Information
                                                </Text>

                                                <Space
                                                    direction="vertical"
                                                    size={13}
                                                    style={{
                                                        width:
                                                            "100%",
                                                        marginTop:
                                                            17
                                                    }}
                                                >
                                                    <Row
                                                        justify="space-between"
                                                    >
                                                        <Text
                                                            style={{
                                                                color:
                                                                    "rgba(255,255,255,0.50)"
                                                            }}
                                                        >
                                                            Student
                                                        </Text>

                                                        <Text
                                                            style={{
                                                                color:
                                                                    "#fff",
                                                                fontWeight:
                                                                    600
                                                            }}
                                                        >
                                                            {selectedStudent.full_name}
                                                        </Text>
                                                    </Row>

                                                    <Row
                                                        justify="space-between"
                                                    >
                                                        <Text
                                                            style={{
                                                                color:
                                                                    "rgba(255,255,255,0.50)"
                                                            }}
                                                        >
                                                            Academic Year
                                                        </Text>

                                                        <Text
                                                            style={{
                                                                color:
                                                                    "#fff",
                                                                fontWeight:
                                                                    600
                                                            }}
                                                        >
                                                            {selectedYear?.name ||
                                                                "N/A"}
                                                        </Text>
                                                    </Row>

                                                    <Row
                                                        justify="space-between"
                                                    >
                                                        <Text
                                                            style={{
                                                                color:
                                                                    "rgba(255,255,255,0.50)"
                                                            }}
                                                        >
                                                            Generated
                                                        </Text>

                                                        <Text
                                                            style={{
                                                                color:
                                                                    "#fff",
                                                                fontWeight:
                                                                    600
                                                            }}
                                                        >
                                                            {new Date().toLocaleString()}
                                                        </Text>
                                                    </Row>

                                                    <Row
                                                        justify="space-between"
                                                        align="middle"
                                                    >
                                                        <Text
                                                            style={{
                                                                color:
                                                                    "rgba(255,255,255,0.50)"
                                                            }}
                                                        >
                                                            Status
                                                        </Text>

                                                        <Tag
                                                            color="green"
                                                            style={{
                                                                borderRadius:
                                                                    20,
                                                                fontWeight:
                                                                    700
                                                            }}
                                                        >
                                                            VERIFIED
                                                        </Tag>
                                                    </Row>
                                                </Space>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card>
                            )}

                        <div
                            style={{
                                marginTop: 25,
                                padding:
                                    "15px 4px 0",
                                textAlign:
                                    "center"
                            }}
                        >
                            <Space
                                size={8}
                                wrap
                                style={{
                                    justifyContent:
                                        "center"
                                }}
                            >
                                <SafetyCertificateOutlined
                                    style={{
                                        color:
                                            COLORS.green
                                    }}
                                />

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            12
                                    }}
                                >
                                    Official Student Financial
                                    Statement
                                </Text>

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            12
                                    }}
                                >
                                    •
                                </Text>

                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            12
                                    }}
                                >
                                    Academic Year:{" "}
                                    {selectedYear?.name ||
                                        "N/A"}
                                </Text>
                            </Space>

                            <Text
                                type="secondary"
                                style={{
                                    display:
                                        "block",
                                    marginTop:
                                        5,
                                    fontSize:
                                        11
                                }}
                            >
                                Design by Polu Integrated
                                School Management System
                            </Text>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default StudentPaymentHistory;

